import { ArrowLeft, Check, Loader2, Pencil, Trash2, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  useCollegaHandler,
  useCreaTipo,
  useEliminaTipo,
  useHandlersMemoria,
  useMergeTipo,
  useModificaTipo,
  useTipiRegola,
} from "@/hooks/useMemoria";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMe } from "@/hooks/useMe";
import { cn } from "@/lib/utils";
import type { TipoRegola } from "@/types/memoria";

export function MemoriaTipiPage() {
  const [tab, setTab] = useState<"tipi" | "permessi">("tipi");

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <Link to="/memoria" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Memoria
      </Link>
      <h1 className="text-2xl font-bold tracking-tight">Tipi e permessi</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Le categorie di regola e chi può crearle/confermarle.
      </p>

      <div className="my-4 inline-flex rounded-lg bg-secondary p-1 text-sm">
        {(["tipi", "permessi"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "rounded-md px-4 py-1.5 font-medium capitalize transition-colors",
              tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            )}
          >
            {t === "tipi" ? "Tipi di regola" : "Permessi"}
          </button>
        ))}
      </div>

      {tab === "tipi" ? <TipiTab /> : <PermessiTab />}
    </div>
  );
}

function TipiTab() {
  const tipi = useTipiRegola();
  const handlers = useHandlersMemoria();
  const { data: me } = useMe();
  const collega = useCollegaHandler();
  const crea = useCreaTipo();
  const merge = useMergeTipo();
  const modifica = useModificaTipo();
  const elimina = useEliminaTipo();
  const [nuovo, setNuovo] = useState("");
  const [simili, setSimili] = useState<TipoRegola[]>([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState("");
  const [daEliminare, setDaEliminare] = useState<TipoRegola | null>(null);

  const salvaRinomina = (tipoId: string) => {
    const et = editVal.trim();
    if (!et) return;
    modifica.mutate({ tipoId, etichetta: et }, { onSuccess: () => setEditId(null) });
  };

  const confermaElimina = () => {
    if (!daEliminare) return;
    elimina.mutate(daEliminare.id, { onSuccess: () => setDaEliminare(null) });
  };

  const puoiGestire = ["maitime_staff", "mp_logistica", "mp_presidenza", "mp_management"].includes(
    me?.ruolo ?? ""
  );

  const creaTipo = (forza: boolean) => {
    const et = nuovo.trim();
    if (!et) return;
    crea.mutate(
      { etichetta: et, forza },
      {
        onSuccess: (res) => {
          if (res.creato) {
            setNuovo("");
            setSimili([]);
          } else {
            setSimili(res.simili);
          }
        },
      }
    );
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <input
          value={nuovo}
          onChange={(e) => setNuovo(e.target.value)}
          placeholder="Nuovo tipo (es. imballo)…"
          className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm focus:bg-background"
        />
        <button
          type="button"
          onClick={() => creaTipo(false)}
          disabled={crea.isPending}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Crea tipo
        </button>
      </div>

      {simili.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-medium text-amber-900">Possibile doppione</p>
          <p className="mt-0.5 text-amber-800">
            Esistono già tipi simili: {simili.map((s) => `«${s.etichetta}»`).join(", ")}. Riusane uno
            o crea comunque.
          </p>
          <button
            type="button"
            onClick={() => creaTipo(true)}
            className="mt-2 rounded-md border border-amber-400 px-2.5 py-1 text-xs font-medium text-amber-900"
          >
            Crea comunque «{nuovo}»
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Etichetta</th>
              <th className="px-4 py-2.5 text-left font-medium">Origine</th>
              <th className="px-4 py-2.5 text-left font-medium">Azionabile</th>
              <th className="px-4 py-2.5 text-left font-medium">Handler</th>
              {puoiGestire && <th className="px-4 py-2.5 text-right font-medium">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {tipi.isLoading ? (
              <tr>
                <td colSpan={puoiGestire ? 5 : 4} className="px-4 py-10 text-center">
                  <Loader2 className="mx-auto size-5 animate-spin text-muted-foreground" />
                </td>
              </tr>
            ) : (
              (tipi.data ?? []).map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="px-4 py-3 font-medium">
                    {editId === t.id ? (
                      <input
                        autoFocus
                        value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") salvaRinomina(t.id);
                          if (e.key === "Escape") setEditId(null);
                        }}
                        className="w-full rounded-md border bg-background px-2 py-1 text-sm"
                      />
                    ) : (
                      t.etichetta
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">
                      {t.creato_da === "ai" ? "AI" : "Umano"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {t.azionabile ? (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        incide sul carico
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">informativa</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {puoiGestire ? (
                      <select
                        value={t.handler_codice ?? ""}
                        onChange={(e) =>
                          collega.mutate({ tipoId: t.id, handler: e.target.value || null })
                        }
                        className="rounded-md border bg-background px-2 py-1 text-xs"
                      >
                        <option value="">— nessuno —</option>
                        {(handlers.data ?? []).map((h) => (
                          <option key={h.codice} value={h.codice}>
                            {h.codice}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {t.handler_codice ?? "—"}
                      </span>
                    )}
                  </td>
                  {puoiGestire && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {editId === t.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => salvaRinomina(t.id)}
                              title="Salva"
                              className="rounded p-1 text-primary hover:bg-muted"
                            >
                              <Check className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditId(null)}
                              title="Annulla"
                              className="rounded p-1 text-muted-foreground hover:bg-muted"
                            >
                              <X className="size-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setEditId(t.id);
                                setEditVal(t.etichetta);
                              }}
                              title="Rinomina"
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                            >
                              <Pencil className="size-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDaEliminare(t)}
                              title="Elimina"
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {puoiGestire && (
        <p className="mt-2 text-xs text-muted-foreground">
          Collegare un handler rende il tipo «azionabile» (incide sul calcolo del carico).
          Riservato a chi gestisce le regole (logistica/direzione/staff).
        </p>
      )}
      {merge.isError && <p className="mt-2 text-xs text-destructive">Merge non riuscito.</p>}

      <Dialog open={!!daEliminare} onOpenChange={(o) => !o && setDaEliminare(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminare il tipo?</DialogTitle>
            <DialogDescription>
              Stai per eliminare «{daEliminare?.etichetta}». Se delle regole lo usano verrà
              nascosto (le regole restano valide); se è libero sarà rimosso del tutto.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDaEliminare(null)}
              className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={confermaElimina}
              disabled={elimina.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {elimina.isPending && <Loader2 className="size-4 animate-spin" />}
              Elimina
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PermessiTab() {
  const righe = [
    { ruolo: "Logistica (Marina)", crea: true, conferma: true },
    { ruolo: "Direzione", crea: true, conferma: true },
    { ruolo: "Staff Maitime", crea: true, conferma: true },
    { ruolo: "Planning / altri", crea: false, conferma: false },
  ];
  return (
    <div>
      <div className="overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Ruolo</th>
              <th className="px-4 py-2.5 text-center font-medium">Crea regole</th>
              <th className="px-4 py-2.5 text-center font-medium">Conferma regole</th>
            </tr>
          </thead>
          <tbody>
            {righe.map((r) => (
              <tr key={r.ruolo} className="border-t">
                <td className="px-4 py-3 font-medium">{r.ruolo}</td>
                <td className="px-4 py-3 text-center">{r.crea ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-center">{r.conferma ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Permessi per agente «Aida» (chiavi <code>memoria_aida_crea</code> /{" "}
        <code>memoria_aida_conferma</code>). La regolazione fine per singolo utente è in{" "}
        <Link to="/admin/utenti" className="underline">
          Admin → Utenti / Permessi
        </Link>
        . La matrice editabile utente×agente è un fast-follow.
      </p>
    </div>
  );
}
